/**
 * HomePage - Lavorazione App
 *
 * Pagina iniziale dell'app Lavorazione che mostra:
 * - Selezione tipo entità (Turbine, FAP, Iniettore)
 * - Statistiche recenti
 * - Azioni rapide
 */

import { useNavigate } from 'react-router-dom';

interface EntityTypeCard {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  count: number; // TODO: Da API
}

function HomePage() {
  const navigate = useNavigate();

  const entityTypes: EntityTypeCard[] = [
    {
      id: 'turbine',
      name: 'Turbine',
      description: 'Gestione turbine geometria variabile',
      icon: '⚙️',
      color: 'bg-gradient-to-br from-blue-500 to-blue-700',
      count: 42 // TODO: Da API
    },
    {
      id: 'fap',
      name: 'FAP',
      description: 'Filtro Antiparticolato Diesel',
      icon: '🔥',
      color: 'bg-gradient-to-br from-orange-500 to-orange-700',
      count: 15 // TODO: Da API
    },
    {
      id: 'iniettore',
      name: 'Iniettori',
      description: 'Iniettori Common Rail',
      icon: '💉',
      color: 'bg-gradient-to-br from-green-500 to-green-700',
      count: 28 // TODO: Da API
    }
  ];

  const handleSelectEntity = (_entityId: string) => {
    // Naviga alla lista entità
    navigate('/apps/lavorazione/list');
  };

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Gestione Lavorazione
        </h2>
        <p className="text-gray-600">
          Seleziona il tipo di componente da lavorare
        </p>
      </div>

      {/* Entity Types Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {entityTypes.map(entity => (
          <button
            key={entity.id}
            onClick={() => handleSelectEntity(entity.id)}
            className={`${entity.color} text-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all hover:-translate-y-1 group relative overflow-hidden`}
          >
            {/* Count Badge */}
            <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm text-white text-sm font-bold rounded-full px-3 py-1">
              {entity.count}
            </div>

            {/* Icon */}
            <div className="text-6xl mb-4 transform group-hover:scale-110 transition-transform">
              {entity.icon}
            </div>

            {/* Name */}
            <h3 className="text-xl font-bold mb-2">{entity.name}</h3>

            {/* Description */}
            <p className="text-white/90 text-sm">{entity.description}</p>

            {/* Hover effect */}
            <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          Azioni Rapide
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button className="p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left group">
            <div className="flex items-center gap-3">
              <div className="text-3xl">📷</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900 group-hover:text-blue-600">
                  Scan QR Code
                </div>
                <div className="text-sm text-gray-500">
                  Scansiona QR per aprire lavorazione
                </div>
              </div>
            </div>
          </button>

          <button className="p-4 border-2 border-gray-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-all text-left group">
            <div className="flex items-center gap-3">
              <div className="text-3xl">➕</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900 group-hover:text-green-600">
                  Nuova Lavorazione
                </div>
                <div className="text-sm text-gray-500">
                  Crea una nuova scheda lavorazione
                </div>
              </div>
            </div>
          </button>

          <button
            onClick={() => navigate('/apps/lavorazione/list')}
            className="p-4 border-2 border-gray-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-all text-left group"
          >
            <div className="flex items-center gap-3">
              <div className="text-3xl">🔍</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900 group-hover:text-purple-600">
                  Vedi Lista
                </div>
                <div className="text-sm text-gray-500">
                  Visualizza e cerca lavorazioni esistenti
                </div>
              </div>
            </div>
          </button>

          <button className="p-4 border-2 border-gray-200 rounded-xl hover:border-orange-500 hover:bg-orange-50 transition-all text-left group">
            <div className="flex items-center gap-3">
              <div className="text-3xl">📊</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900 group-hover:text-orange-600">
                  Statistiche
                </div>
                <div className="text-sm text-gray-500">
                  Vedi report e statistiche
                </div>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <div className="text-2xl">🚧</div>
          <div>
            <h4 className="font-semibold text-blue-900 mb-1">
              App in Sviluppo
            </h4>
            <p className="text-sm text-blue-700">
              Questa è la versione base dell'app Lavorazione. Le funzionalità complete
              (scan QR, gestione entità, photo sessions) verranno implementate nelle
              prossime iterazioni.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HomePage;
